import { ref } from 'vue'
import { apiFetch } from '../api'

/**
 * Keeping the stream plates on the boat.
 *
 * Thirteen plates an atlas, ten atlases, about twenty-five megabytes in all: too much to
 * push at every visitor of the website, but exactly what a registered skipper wants on board.
 * So once a device is registered the app fetches every atlas in the background after each
 * sync (`holdAll`), skipping what it already has, and keeps them in a cache of its own. No
 * network at sea, and the plates are still there, whichever area the boat ends up in. The
 * atlas a skipper picks is fetched first (`hold`).
 *
 * Holding them ourselves also fixes the order in which things go when the browser starts
 * pressing for room: the tide predictions are the app, and the plates are a convenience.
 * A quota complaint anywhere else in the app can call `freePlates` and try again.
 *
 * The plates come from the API, with the device's token: a device without a registration
 * gets the plate for high water and is refused the rest. An <img> cannot send a token, so
 * the picture on screen is made from the fetched bytes (`plateImage`).
 */

const CACHE = 'quicktide-stroom'
/** Where the app once noted which atlases it had used; only cleared now */
const SEEN = 'quicktide.atlases'
const HOURS = [-6, -5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5, 6]

const plateName = (hour: number) => (hour === 0 ? 'hw' : hour > 0 ? `p${hour}` : `m${-hour}`)
const platePath = (atlas: string, hour: number) => `/stroom/${atlas}/${plateName(hour)}.webp`
/** The cache keys: the API's own address for each plate */
const plateKey = (atlas: string, hour: number) => `/api/v1${platePath(atlas, hour)}`
const plateUrls = (atlas: string) => HOURS.map(h => plateKey(atlas, h))

/** How far the current atlas has got, for anything that wants to show it */
export const holding = ref<{ atlas: string; done: number; total: number } | null>(null)

const available = () => 'caches' in self && typeof caches?.open === 'function'

/** Plates kept under their old addresses, from before they came through the API */
async function dropOldKeys(cache: Cache): Promise<void> {
  for (const request of await cache.keys()) {
    if (new URL(request.url).pathname.startsWith('/stroom/')) await cache.delete(request)
  }
}

/**
 * One plate as a picture the page can show: from the cache when it is held, else from the
 * API, kept for next time. 'denied' when this device may not have it, null when it cannot be
 * had at all just now (offline, or not there).
 */
export async function plateImage(atlas: string, hour: number): Promise<Blob | 'denied' | null> {
  const key = plateKey(atlas, hour)
  const cache = available() ? await caches.open(CACHE).catch(() => null) : null
  const held = await cache?.match(key)
  if (held) return held.blob()
  try {
    const response = await apiFetch(platePath(atlas, hour))
    if (response.status === 403) return 'denied'
    if (!response.ok) return null
    if (cache) await cache.put(key, response.clone()).catch(() => undefined)
    return response.blob()
  } catch {
    return null
  }
}

const isQuota = (e: unknown) => e instanceof Error && (e.name === 'QuotaExceededError' || /quota/i.test(e.message))

/**
 * Fetch every plate of an atlas into the cache, skipping what is already there.
 * Quiet about failure: an atlas that could not be fetched simply is not held, and the
 * plates still load over the network whenever there is one. 'full' when the browser would
 * not give the room, which is a reason to stop asking for more.
 */
export async function hold(atlas: string): Promise<'full' | void> {
  if (!available() || !navigator.onLine) return
  try {
    const cache = await caches.open(CACHE)
    await dropOldKeys(cache)
    const missing: string[] = []
    for (const url of plateUrls(atlas)) if (!(await cache.match(url))) missing.push(url)
    if (!missing.length) return

    holding.value = { atlas, done: 0, total: missing.length }
    for (const url of missing) {
      // one at a time: this is background work behind a picture the reader already has
      try {
        const response = await apiFetch(url.slice('/api/v1'.length))
        if (response.ok) await cache.put(url, response)
      } catch (e) {
        if (isQuota(e)) return 'full'
        // offline again, or the server is down; what is held is held
      }
      if (holding.value?.atlas !== atlas) return          // another atlas was picked meanwhile
      holding.value = { atlas, done: holding.value.done + 1, total: missing.length }
    }
  } catch (e) {
    if (isQuota(e)) return 'full'
    // caches are unavailable in this context
  } finally {
    if (holding.value?.atlas === atlas) holding.value = null
  }
}

let holdingAll = false

/**
 * Every atlas, one after the other, for a registered device: run after each sync and
 * cheap when they are all there already. Stops when the browser runs out of room -- the
 * tide predictions need it more -- or the connection goes.
 */
export async function holdAll(atlases: string[]): Promise<void> {
  if (holdingAll) return
  holdingAll = true
  try {
    for (const atlas of atlases) {
      if (!navigator.onLine) return
      if (await hold(atlas) === 'full') return
    }
  } finally {
    holdingAll = false
  }
}

/** Give the room back. The plates are a convenience; the predictions are the app. */
export async function freePlates(): Promise<number> {
  if (!available()) return 0
  const cache = await caches.open(CACHE)
  const keys = await cache.keys()
  for (const key of keys) await cache.delete(key)
  try {
    localStorage.removeItem(SEEN)
  } catch {
    // nothing to do about it
  }
  return keys.length
}

/** What the held plates take up, for the storage list in Settings */
export async function heldBytes(): Promise<{ atlases: number; bytes: number }> {
  if (!available()) return { atlases: 0, bytes: 0 }
  try {
    const cache = await caches.open(CACHE)
    const keys = await cache.keys()
    let bytes = 0
    for (const key of keys) {
      const response = await cache.match(key)
      const length = response?.headers.get('content-length')
      if (length) bytes += Number(length)
      else if (response) bytes += (await response.clone().blob()).size
    }
    const atlases = new Set(keys.map(k => new URL(k.url).pathname.split('/').at(-2)))
    return { atlases: atlases.size, bytes }
  } catch {
    return { atlases: 0, bytes: 0 }
  }
}
